import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import equal from 'fast-deep-equal';
import {Layout, Spacer, Text} from '@di-nxt/components';
import {DataContainer} from '.';
import {DataRow, DataRowSaveCancelButtons} from '../../../components';
import {t} from '../../../localization';
import {useWarnLeavePage} from '../../../navigation/hooks';
import {TeamMember, useMembershipsOperations} from '../../../services';

type Props<TEditKey extends keyof TeamMember = keyof TeamMember> = {
  entity: TeamMember;
  title: string;
  description?: string;
  editKey: TEditKey;
  disabled: boolean;
  onEditModeChanged?: (t: boolean) => void;
  onGetDisplayValue: (editState?: TeamMember[TEditKey]) => string;
  children: (
    editState: TeamMember[TEditKey] | undefined,
    setEditState: React.Dispatch<
      React.SetStateAction<TeamMember[TEditKey] | undefined>
    >,
  ) => React.ReactNode;
};

export const MemberFieldEditor = <
  TEditKey extends keyof TeamMember = keyof TeamMember
>(
  props: Props<TEditKey>,
) => {
  const {
    editKey,
    title,
    description,
    entity,
    disabled,
    onEditModeChanged,
    onGetDisplayValue,
    children,
  } = props;

  // Operation
  const {update} = useMembershipsOperations();

  // Editor states
  const [editState, setEditState] = useState(
    entity ? entity[editKey] : undefined,
  );

  // Handle warning on navigate away
  useWarnLeavePage(
    t('screens.member.warning.title'),
    t('screens.member.warning.description'),
    () => !equal(editState, entity ? entity[editKey] : undefined),
  );

  // Save and update entity value
  const handleSave = useCallback(
    (dismiss: () => void) => {
      // Update
      return entity
        ? update(entity.membershipId, {
            [editKey]: editState,
          }).then(() => {
            // Close
            dismiss();
          })
        : undefined;
    },
    [entity, update, editKey, editState],
  );

  // Cancel and reset
  const handleDismiss = useCallback(
    (dismiss: () => void) => {
      // Reset
      setEditState(entity ? entity[editKey] : undefined);
      // Close
      dismiss();
    },
    [editKey, entity],
  );

  // Update edit state when entity changes
  // Should NOT update when we are currently editing
  const isEditingRef = useRef<boolean>(false);
  useEffect(() => {
    isEditingRef.current === false &&
      setEditState(entity ? entity[editKey] : undefined);
  }, [editKey, entity]);

  // Formatted display value
  const displayValue = useMemo(
    () => onGetDisplayValue(entity ? entity[editKey] : undefined),
    [editKey, entity, onGetDisplayValue],
  );

  // Reset editing flag
  isEditingRef.current = false;

  return (
    <DataContainer>
      <DataRow
        label={title}
        data={displayValue}
        kind="Edit"
        disabled={disabled}
        onEditModeChanged={onEditModeChanged}>
        {dismiss => {
          // Set editing flag
          isEditingRef.current = true;
          return (
            <Layout vertical>
              {description && (
                <Text
                  variant="paragraph_14_default"
                  textColor="content_mono_secondary">
                  {description}
                </Text>
              )}
              <Spacer height="16" />
              {children(editState, setEditState)}
              <DataRowSaveCancelButtons
                onCancel={() => handleDismiss(dismiss)}
                onSave={() => handleSave(dismiss)}
              />
            </Layout>
          );
        }}
      </DataRow>
    </DataContainer>
  );
};
